﻿/**
 * Product Gallery Thumbnail Toggle
 * Handles showing/hiding thumbnail gallery with smooth transitions
 */

(function() {
  'use strict';

  // DOM Elements
  let toggleBtn = null;
  let gallery = null;
  let galleryContainer = null;
  let scrollLeftBtn = null;
  let scrollRightBtn = null;
  let isGalleryVisible = false;
  let hasInitialized = false;
  let hasBoundListeners = false;
  let isNavigating = false; // Prevent rapid clicks
  let currentActiveIndex = 0; // Track current index
  let domObserver = null;
  let initRetryTimer = null;
  let missingElementsLogged = false;
  let initRetryCount = 0;
  const MAX_INIT_RETRIES = 15;
  let _galleryKeydownHandler = null;

  /**
   * Initialize the thumbnail toggle functionality
   */
  function initThumbnailToggle() {
    // Get DOM elements (support both metafield gallery and dynamic gallery)
    toggleBtn = document.getElementById('toggle-thumbnails');
    gallery = document.getElementById('thumbnail-gallery');
    galleryContainer = document.getElementById('thumbnail-gallery-container') || document.getElementById('dynamic-gallery-thumbnails');
    scrollLeftBtn = document.getElementById('thumb-left');
    scrollRightBtn = document.getElementById('thumb-right');

    if (!toggleBtn || !gallery) {
      // Gallery might be injected later (dynamic gallery). Observe DOM and retry.
      ensureDomObserver();
      scheduleInitRetry();
      if (!missingElementsLogged) {
        missingElementsLogged = true;
        console.log('⚠️ Thumbnail toggle elements not found');
      }
      return;
    }

    // Reset retry tracking once elements are found
    missingElementsLogged = false;
    initRetryCount = 0;

    if (!hasInitialized) {
      hasInitialized = true;
    }

    console.log('✓ Initializing thumbnail gallery toggle');

    // Start hidden by default ("More images" state)
    isGalleryVisible = false;
    // Force an immediate hidden state to avoid flash / broken half-visible layout
    forceHiddenImmediately();
    hideGallery();
    updateButtonText();
    updateAriaAttributes();

    // Add click/keyboard event listeners (only once)
    if (!hasBoundListeners) {
      hasBoundListeners = true;

      toggleBtn.addEventListener('click', handleToggleClick);

      // Add keyboard accessibility
      toggleBtn.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggleClick();
        }
      });
    }

    // Initialize scroll buttons
    initScrollButtons();

    // Ensure we re-bind if the gallery is dynamically created/replaced later
    ensureDomObserver();

    console.log('✅ Thumbnail gallery toggle initialized');
  }

  function scheduleInitRetry() {
    if (initRetryTimer) return;

    if (initRetryCount >= MAX_INIT_RETRIES) {
      // Stop retrying forever when toggle isn't included on this template.
      return;
    }
    initRetryCount++;

    initRetryTimer = setTimeout(() => {
      initRetryTimer = null;
      // Reset DOM refs only, keep listeners flag since it is tied to elements.
      reacquireDom();
      // If the elements changed, allow rebinding.
      hasBoundListeners = false;
      initThumbnailToggle();
    }, 200);
  }

  function ensureDomObserver() {
    if (domObserver) return;
    domObserver = new MutationObserver(() => {
      // If toggle or gallery nodes are replaced, reacquire and re-init.
      const nextToggle = document.getElementById('toggle-thumbnails');
      const nextGallery = document.getElementById('thumbnail-gallery');
      if (nextToggle !== toggleBtn || nextGallery !== gallery) {
        reacquireDom();
        hasBoundListeners = false;
        initThumbnailToggle();
      }
    });
    domObserver.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true
    });
  }

  function reacquireDom() {
    toggleBtn = document.getElementById('toggle-thumbnails');
    gallery = document.getElementById('thumbnail-gallery');
    galleryContainer = document.getElementById('thumbnail-gallery-container') || document.getElementById('dynamic-gallery-thumbnails');
    scrollLeftBtn = document.getElementById('thumb-left');
    scrollRightBtn = document.getElementById('thumb-right');
  }

  function forceHiddenImmediately() {
    if (!gallery) return;
    gallery.style.display = 'none';
    gallery.style.maxHeight = '0';
    gallery.style.opacity = '0';
    gallery.style.overflow = 'hidden';
    gallery.classList.add('hidden');
  }

  /**
   * Initialize scroll button functionality
   */
  function initScrollButtons() {
    if (scrollLeftBtn) {
      scrollLeftBtn.addEventListener('click', () => scrollGallery(-1));
      // Add keyboard support
      scrollLeftBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollGallery(-1);
        }
      });
    }

    if (scrollRightBtn) {
      scrollRightBtn.addEventListener('click', () => scrollGallery(1));
      // Add keyboard support
      scrollRightBtn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollGallery(1);
        }
      });
    }

    // Add arrow key navigation (remove previous handler first to prevent accumulation)
    if (_galleryKeydownHandler) {
      document.removeEventListener('keydown', _galleryKeydownHandler);
    }
    _galleryKeydownHandler = (e) => {
      if (!isGalleryVisible) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollGallery(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollGallery(1);
      }
    };
    document.addEventListener('keydown', _galleryKeydownHandler);
  }

  /**
   * Get all visible thumbnails (cached for performance)
   */
  function getVisibleThumbnails() {
    if (!gallery) return [];
    return Array.from(gallery.querySelectorAll('.thumbnail-item:not(.hidden) img, [data-dynamic-gallery-inner] img'));
  }

  /**
   * Find current active thumbnail index
   */
  function findActiveIndex(thumbnails) {
    for (let i = 0; i < thumbnails.length; i++) {
      // Check for various active state classes used in the gallery
      if (thumbnails[i].classList.contains('border-blue-500') || 
          thumbnails[i].classList.contains('border-cyan-400') ||
          thumbnails[i].classList.contains('border-cyan-500') ||
          thumbnails[i].classList.contains('active-thumbnail') ||
          thumbnails[i].dataset.active === 'true') {
        return i;
      }
    }
    return currentActiveIndex; // Fallback to tracked index
  }

  /**
   * Update thumbnail active state - ensures only ONE thumbnail is highlighted
   */
  function updateThumbnailState(thumbnails, newIndex) {
    // Remove all active states and dim all thumbnails
    thumbnails.forEach(thumb => {
      thumb.classList.remove('border-blue-500', 'border-cyan-400', 'border-cyan-500', 'active-thumbnail');
      thumb.dataset.active = 'false';
      thumb.classList.add('border-transparent');
      thumb.style.opacity = '0.7';
      thumb.style.border = '';
    });

    // Add active state to ONLY the new thumbnail
    const newThumb = thumbnails[newIndex];
    if (newThumb) {
      newThumb.classList.remove('border-transparent');
      newThumb.classList.add('border-cyan-400', 'active-thumbnail');
      newThumb.dataset.active = 'true';
      newThumb.style.opacity = '1';
      newThumb.style.border = '3px solid #22d3ee'; // Cyan border
      currentActiveIndex = newIndex;
    }

    return newThumb;
  }

  // C2: Module-scoped token + AbortController prevents stale image-load
  // handlers from firing against a newer swap. Without these, a slow-loading
  // A-image's load handler runs AFTER B is displayed and strips the loading
  // state (and on older code paths, could overwrite UI) against the wrong
  // variant.
  let __updateMainImageToken = 0;
  let __updateMainImageAbort = null;

  /**
   * Update main product image with loading indicators
   */
  function updateMainImage(thumbnail) {
    if (!thumbnail || !thumbnail.src) return false;

    const productImage = document.getElementById('product-image');
    if (!productImage) return false;

    const token = ++__updateMainImageToken;
    productImage.dataset.loadToken = String(token);

    if (__updateMainImageAbort) {
      try { __updateMainImageAbort.abort(); } catch (_) { /* ignore */ }
    }
    __updateMainImageAbort = new AbortController();
    const signal = __updateMainImageAbort.signal;

    // Show loading state
    showImageLoadingState(productImage);

    const handleLoad = () => {
      if (signal.aborted) return;
      if (productImage.dataset.loadToken !== String(token)) return;
      hideImageLoadingState(productImage);
    };

    const handleError = () => {
      if (signal.aborted) return;
      if (productImage.dataset.loadToken !== String(token)) return;
      hideImageLoadingState(productImage);
    };

    productImage.addEventListener('load', handleLoad, { signal });
    productImage.addEventListener('error', handleError, { signal });

    // Update image source
    productImage.src = thumbnail.src;
    return true;
  }

  /**
   * Show loading state for image
   */
  function showImageLoadingState(imageElement) {
    // Add blur effect
    imageElement.style.filter = 'blur(2px)';
    imageElement.style.opacity = '0.7';

    // Create or update loading overlay
    let loadingOverlay = imageElement.parentElement.querySelector('.image-loading-overlay');
    if (!loadingOverlay) {
      loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'image-loading-overlay';
      loadingOverlay.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 255, 255, 0.9);
        padding: 12px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        z-index: 10;
        pointer-events: none;
      `;
      loadingOverlay.textContent = 'Loading...';

      // Position relative to image container
      const imageContainer = imageElement.parentElement;
      if (imageContainer) {
        imageContainer.style.position = 'relative';
        imageContainer.appendChild(loadingOverlay);
      }
    }

    loadingOverlay.style.display = 'block';
  }

  /**
   * Hide loading state for image
   */
  function hideImageLoadingState(imageElement) {
    // Remove blur and restore opacity
    imageElement.style.filter = '';
    imageElement.style.opacity = '';

    // Hide loading overlay
    const loadingOverlay = imageElement.parentElement?.querySelector('.image-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
  }


  /**
   * Normalize malformed side names (handles filenames with missing first letter)
   * e.g., 'ront' -> 'front', 'ack' -> 'back', 'ide' -> 'side'
   * Also maps 'left'/'right' to 'left-side'/'right-side' to match print area config
   */
  function normalizeSide(side) {
    if (!side) return side;
    // Use global normalizeSideName if available (from configurator-integration)
    if (typeof window.normalizeSideName === 'function') {
      return window.normalizeSideName(side);
    }
    // Fallback inline normalization
    const s = String(side).toLowerCase().trim();
    if (s === 'ront' || s === 'front') return 'front';
    if (s === 'ack' || s === 'back') return 'back';
    if (s === 'ide' || s === 'side') return 'side';
    // Map 'left' to 'left-side' and 'right' to 'right-side' to match print area configuration
    if (s === 'eft' || s === 'left' || s === 'leftside') return 'left-side';
    if (s === 'ight' || s === 'right' || s === 'rightside') return 'right-side';
    return s;
  }

  /**
   * Extract side name from thumbnail element
   * Checks class names, alt text, and src for side identification
   */
  function extractSideFromThumbnail(thumb) {
    if (!thumb) return null;
    
    // Method 0: Use parseImageAlt if available (handles malformed alt text)
    if (typeof window.parseImageAlt === 'function' && thumb.alt) {
      const parsed = window.parseImageAlt(thumb.alt);
      if (parsed && parsed.side) {
        return parsed.side;
      }
    }
    
    // Method 1: Check for side-specific class (e.g., 'front-thumbnail', 'back-thumbnail')
    const sideClass = Array.from(thumb.classList).find(c => c.endsWith('-thumbnail'));
    if (sideClass) {
      const side = sideClass.replace('-thumbnail', '');
      if (['front', 'back', 'left-side', 'right-side', 'side'].includes(side)) {
        return side;
      }
      // Handle legacy class names
      if (side === 'left') return 'left-side';
      if (side === 'right') return 'right-side';
    }
    
    // Method 2: Check alt text pattern mockup-{color}-{side}
    const alt = (thumb.alt || '').toLowerCase();
    const altMatch = alt.match(/mockup-[^-]+-(.+)$/i);
    if (altMatch) {
      return normalizeSide(altMatch[1]);
    }
    // Fallback: simple keyword check with proper side mapping
    if (alt.includes('front')) return 'front';
    if (alt.includes('back')) return 'back';
    if (alt.includes('leftside')) return 'left-side';
    if (alt.includes('rightside')) return 'right-side';
    if (alt.includes('left-side')) return 'left-side';
    if (alt.includes('right-side')) return 'right-side';
    if (alt.includes('left')) return 'left-side';  // Map to left-side
    if (alt.includes('right')) return 'right-side';  // Map to right-side
    if (alt.includes('side')) return 'side';
    
    // Method 3: Check src URL for side indicators (e.g., '_front_', '_back_', '_ront', '_ack')
    const src = (thumb.src || '').toLowerCase();
    // Extract side from filename pattern: _colorname_sidename.ext
    const srcMatch = src.match(/_([^_]+)\.(jpg|jpeg|png|webp|gif)/i);
    if (srcMatch) {
      const potentialSide = normalizeSide(srcMatch[1]);
      if (['front', 'back', 'left-side', 'right-side', 'side'].includes(potentialSide)) {
        return potentialSide;
      }
    }
    // Fallback: keyword check in src with proper side mapping
    if (src.includes('_front') || src.includes('-front') || src.includes('ront')) return 'front';
    if (src.includes('_back') || src.includes('-back') || src.includes('ack')) return 'back';
    if (src.includes('_leftside') || src.includes('-leftside')) return 'left-side';
    if (src.includes('_rightside') || src.includes('-rightside')) return 'right-side';
    if (src.includes('_left-side') || src.includes('-left-side')) return 'left-side';
    if (src.includes('_right-side') || src.includes('-right-side')) return 'right-side';
    if (src.includes('_left') || src.includes('-left')) return 'left-side';  // Map to left-side
    if (src.includes('_right') || src.includes('-right')) return 'right-side';  // Map to right-side
    if (src.includes('_side') || src.includes('-side') || src.includes('ide')) return 'side';
    
    // Method 4: Check data attributes
    if (thumb.dataset.side) return normalizeSide(thumb.dataset.side);
    
    // Method 5: Check parent element for side info
    const parent = thumb.closest('[data-side]');
    if (parent?.dataset.side) return normalizeSide(parent.dataset.side);
    
    return null;
  }

  /**
   * Navigate to previous/next thumbnail
   * @param {number} direction - -1 for previous, 1 for next
   */
  function scrollGallery(direction) {
    // Prevent rapid navigation
    if (isNavigating || !gallery) return;
    
    isNavigating = true;

    try {
      const thumbnails = getVisibleThumbnails();
      if (thumbnails.length === 0) {
        console.warn('⚠️ No visible thumbnails found');
        return;
      }

      // Find current active thumbnail
      const currentIndex = findActiveIndex(thumbnails);

      // Calculate new index with wrapping
      let newIndex = currentIndex + direction;
      if (newIndex < 0) newIndex = thumbnails.length - 1;
      if (newIndex >= thumbnails.length) newIndex = 0;

      // Get the new thumbnail
      const newThumb = thumbnails[newIndex];
      if (!newThumb) {
        console.error('❌ Failed to get new thumbnail');
        return;
      }

      // Extract side from the thumbnail
      const side = extractSideFromThumbnail(newThumb);
      
      // IMPORTANT: Use handleThumbnailClick to trigger full side change
      // This updates: image, print area overlay, design overlays, state, etc.
      let sideChanged = false;
      if (side && newThumb.src && typeof window.handleThumbnailClick === 'function') {
        try {
          window.handleThumbnailClick(side, newThumb.src, 0);
          sideChanged = true;
          console.log(`✓ Arrow navigation: triggered full side change to ${side}`);
        } catch (e) {
          console.warn('⚠️ handleThumbnailClick failed:', e.message);
        }
      }
      
      // Fallback: try to trigger the thumbnail's onclick handler directly
      if (!sideChanged) {
        // Check if thumbnail has an onclick attribute (from Liquid template)
        const onclickAttr = newThumb.getAttribute('onclick');
        if (onclickAttr && onclickAttr.includes('handleThumbnailClick')) {
          try {
            // Execute the onclick handler
            const fn = new Function(onclickAttr);
            fn.call(newThumb);
            sideChanged = true;
            console.log(`✓ Arrow navigation: executed onclick handler`);
          } catch (e) {
            console.warn('⚠️ onclick execution failed:', e.message);
          }
        }
      }
      
      // Last resort fallback: just update the image (won't update overlays properly)
      if (!sideChanged) {
        console.warn('⚠️ Could not trigger full side change, falling back to image-only update');
        updateMainImage(newThumb);
      }

      // Update thumbnail active states AFTER the side change
      updateThumbnailState(thumbnails, newIndex);

      // Scroll thumbnail into view smoothly
      newThumb.scrollIntoView({ 
        behavior: 'smooth', 
        inline: 'center', 
        block: 'nearest' 
      });

      // Update gallery counter
      updateGalleryCounter();
      
      console.log(`✓ Navigated to thumbnail ${newIndex + 1}/${thumbnails.length} (side: ${side || 'unknown'})`);
    } catch (error) {
      console.error('❌ Error during gallery navigation:', error);
    } finally {
      // Re-enable navigation after a short delay
      setTimeout(() => {
        isNavigating = false;
      }, 300);
    }
  }

  /**
   * Handle toggle button click
   */
  function handleToggleClick() {
    isGalleryVisible = !isGalleryVisible;

    if (isGalleryVisible) {
      showGallery();
    } else {
      hideGallery();
    }

    updateButtonText();
    updateAriaAttributes();
  }

  /**
   * Show the thumbnail gallery
   */
  function showGallery() {
    if (!gallery) return;

    // Restore display in case it was hard-hidden
    gallery.style.display = '';

    // Remove hidden class and add transition
    gallery.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
    gallery.style.maxHeight = '500px';
    gallery.style.opacity = '1';
    gallery.style.overflow = 'visible';
    gallery.classList.remove('hidden');

    console.log('✓ Gallery shown');
  }

  /**
   * Hide the thumbnail gallery
   */
  function hideGallery() {
    if (!gallery) return;

    // Add transition and hide
    gallery.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
    gallery.style.maxHeight = '0';
    gallery.style.opacity = '0';
    gallery.style.overflow = 'hidden';

    // Add hidden class after transition
    setTimeout(() => {
      if (!isGalleryVisible) {
        gallery.classList.add('hidden');
        gallery.style.display = 'none';
      }
    }, 300);

    console.log('✓ Gallery hidden');
  }

  /**
   * Update button text and icon based on gallery state
   */
  function updateButtonText() {
    if (!toggleBtn) return;

    const toggleText = document.getElementById('toggle-text');
    const toggleIcon = document.getElementById('toggle-icon');

    if (isGalleryVisible) {
      if (toggleText) toggleText.textContent = 'Hide images';
      toggleBtn.setAttribute('aria-label', 'Hide thumbnail gallery');
      toggleBtn.classList.add('expanded');
      
      // Update icon to X
      if (toggleIcon) {
        toggleIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>';
      }
    } else {
      if (toggleText) toggleText.textContent = 'More images';
      toggleBtn.setAttribute('aria-label', 'Show thumbnail gallery');
      toggleBtn.classList.remove('expanded');
      
      // Update icon to grid
      if (toggleIcon) {
        toggleIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>';
      }
    }
    
    // Update gallery counter visibility
    updateGalleryCounter();
  }
  
  /**
   * Update gallery counter badge
   */
  function updateGalleryCounter() {
    const counter = document.getElementById('gallery-counter');
    if (!counter) return;
    
    const thumbnails = getVisibleThumbnails();
    if (thumbnails.length > 0 && isGalleryVisible) {
      const currentIndex = findActiveIndex(thumbnails);
      counter.textContent = `${currentIndex + 1} / ${thumbnails.length}`;
      counter.classList.remove('hidden');
    } else {
      counter.classList.add('hidden');
    }
  }

  /**
   * Update ARIA attributes for accessibility
   */
  function updateAriaAttributes() {
    if (!toggleBtn || !gallery) return;

    toggleBtn.setAttribute('aria-expanded', isGalleryVisible.toString());
    gallery.setAttribute('aria-hidden', (!isGalleryVisible).toString());
  }

  /**
   * Public API
   */
  window.ThumbnailGalleryToggle = {
    init: initThumbnailToggle,
    show: showGallery,
    hide: hideGallery,
    toggle: handleToggleClick,
    reinit: function() {
      // Full reset in case of dynamic DOM changes
      hasInitialized = false;
      hasBoundListeners = false;
      isGalleryVisible = false;
      reacquireDom();
      initThumbnailToggle();
    },
    isVisible: function() { return isGalleryVisible; }
  };

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initThumbnailToggle);
  } else {
    initThumbnailToggle();
  }

  // Clean up document-level listener when Shopify theme editor unloads the section
  document.addEventListener('shopify:section:unload', () => {
    if (_galleryKeydownHandler) {
      document.removeEventListener('keydown', _galleryKeydownHandler);
      _galleryKeydownHandler = null;
    }
    if (domObserver) {
      domObserver.disconnect();
      domObserver = null;
    }
    hasBoundListeners = false;
    hasInitialized = false;
  });

  // Expose unified image update function globally
  window.updateMainImageWithLoading = updateMainImage;

})();
